import React, { useEffect, useMemo, useState } from 'react';
import { AdminLayout } from '../../dashboard/components/AdminLayout';
import { COLORS } from '../../../../core/constants/colors';
import { InvitePartnerModal, InvitePayload } from '../components/InvitePartnerModal';
import { CompanyOption } from '../../../../core/components/CompanyPicker';
import { api } from '../../../../core/utils/apiClient';

interface Partner {
  id: number;
  partner_id: string;
  email: string;
  first_name: string;
  last_name: string;
  company_name: string;
  cognito_user_id: string;
  status: 'pending_approval' | 'active' | 'inactive' | 'suspended';
  invited_at: string;
  activated_at: string | null;
  created_at: string;
  updated_at: string;
  invited_by: string;
  notes: string | null;
}

const SUCCESS_BANNER = {
  background: '#E9FBF4',
  border: '#A6E9D0',
  text: '#027A48',
};

const ERROR_BANNER = {
  background: '#FEF2F2',
  border: '#FECACA',
  text: '#B42318',
};

export const AdminUsersPage: React.FC = () => {
  const [partners, setPartners] = useState<Partner[]>([]);
  const [isLoadingPartners, setIsLoadingPartners] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const [isInviteModalOpen, setInviteModalOpen] = useState(false);
  const [isSendingInvite, setIsSendingInvite] = useState(false);
  const [companies, setCompanies] = useState<CompanyOption[]>([]);
  const [isLoadingCompanies, setIsLoadingCompanies] = useState(false);
  const [inviteBanner, setInviteBanner] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [deleteDialog, setDeleteDialog] = useState<{ isOpen: boolean; partner: Partner | null }>({
    isOpen: false,
    partner: null,
  });
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    loadPartners();
    loadCompanies();
  }, []);

  const loadCompanies = async () => {
    setIsLoadingCompanies(true);
    try {
      // The picker is purely client-side search — if the portfolio grows past
      // this cap, additional companies are invisible. Bumped past current
      // production size; we also detect+warn below if we hit the cap.
      const COMPANY_FETCH_LIMIT = 500;
      const response = await api.get(`/api/v1/companies/?limit=${COMPANY_FETCH_LIMIT}`);
      if (response.ok) {
        const data = await response.json();
        const items = (data.companies || data.items || data || []) as Array<{
          id: number;
          name: string;
          domain?: string | null;
        }>;
        setCompanies(
          items.map((c) => ({ id: c.id, name: c.name, domain: c.domain ?? null }))
        );
        const total = typeof data?.total === 'number' ? data.total : undefined;
        if (total !== undefined && total > items.length) {
          // Visible warning rather than silent truncation. If this fires, the
          // picker needs to move to server-side async search.
          console.warn(
            `Company picker loaded ${items.length}/${total} companies — ` +
              `picker is silently capped. Switch to server-side search.`,
          );
          setInviteBanner({
            type: 'error',
            message:
              `Showing ${items.length} of ${total} companies in the picker. ` +
              "Some companies won't appear in search — contact the dev team to enable server-side search.",
          });
        }
      } else {
        const errMsg = `Companies could not be loaded for the invite picker (HTTP ${response.status}).`;
        console.error(errMsg);
        setInviteBanner({ type: 'error', message: errMsg });
      }
    } catch (error: any) {
      const errMsg =
        'Could not load companies for the invite picker. You can still invite using "Create new company".';
      console.error('Error loading companies for invite picker:', error);
      setInviteBanner({ type: 'error', message: errMsg });
    } finally {
      setIsLoadingCompanies(false);
    }
  };

  const loadPartners = async () => {
    setIsLoadingPartners(true);
    try {
      const response = await api.get('/api/v1/partners');
      if (response.ok) {
        const data = await response.json();
        setPartners(data.partners || []);
      } else {
        console.error('Failed to load partners:', response.status);
      }
    } catch (error) {
      console.error('Error loading partners:', error);
    } finally {
      setIsLoadingPartners(false);
    }
  };

  const filteredPartners = useMemo(() => {
    return partners.filter((partner) => {
      const fullName = `${partner.first_name} ${partner.last_name}`.toLowerCase();
      const matchesSearch =
        fullName.includes(searchQuery.toLowerCase()) ||
        partner.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
        partner.company_name?.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesStatus = filterStatus === 'all' || partner.status === filterStatus;
      return matchesSearch && matchesStatus;
    });
  }, [partners, searchQuery, filterStatus]);

  const paginatedPartners = filteredPartners.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
  const totalPages = Math.ceil(filteredPartners.length / itemsPerPage);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending_approval':
        return { bg: '#FEF3C7', text: '#D97706' };
      case 'active':
        return { bg: '#D1FAE5', text: '#059669' };
      case 'inactive':
        return { bg: '#F3F4F6', text: '#6B7280' };
      case 'suspended':
        return { bg: '#FEE2E2', text: '#DC2626' };
      default:
        return { bg: '#F3F4F6', text: '#6B7280' };
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'pending_approval':
        return 'Pending Approval';
      case 'active':
        return 'Active';
      case 'inactive':
        return 'Inactive';
      case 'suspended':
        return 'Suspended';
      default:
        return status;
    }
  };

  const getInitials = (firstName: string, lastName: string, email: string) => {
    if (firstName && lastName) {
      return `${firstName[0]}${lastName[0]}`.toUpperCase();
    }
    if (firstName) {
      return firstName.substring(0, 2).toUpperCase();
    }
    return email.substring(0, 2).toUpperCase();
  };

  const getInitialColor = (name: string) => {
    const colors = [
      COLORS.primary.violet,
      COLORS.extended.orange,
      COLORS.admin.primary,
      COLORS.primary.deepSoftLime,
      COLORS.extended.lavenderBlue,
      COLORS.primary.softLime,
    ];
    const index = name?.charCodeAt(0) % colors.length || 0;
    return colors[index];
  };

  const handleInvitePartner = async (payload: InvitePayload) => {
    setIsSendingInvite(true);
    try {
      // Forward exactly what the modal produced — either company_id (existing)
      // or new_company (create + link). Backend keeps company_name as a legacy
      // fallback but neither is sent here.
      const body: Record<string, unknown> = {
        email: payload.email,
        first_name: payload.first_name,
        last_name: payload.last_name,
        notes: payload.notes || undefined,
      };
      if (payload.company_id != null) {
        body.company_id = payload.company_id;
      } else if (payload.new_company) {
        body.new_company = payload.new_company;
      }

      const response = await api.post('/api/v1/partners/invitations', body);

      if (response.ok) {
        const data = await response.json();
        setInviteBanner({
          type: 'success',
          message: data.message || `Invitation sent to ${payload.email}. They will receive an email with steps to set their password.`,
        });
        setInviteModalOpen(false);
        // Reload partners list to show the new invitation. Also refresh the
        // company picker since "Create new" may have added a company.
        await Promise.all([loadPartners(), loadCompanies()]);
      } else {
        const errorData = await response.json().catch(() => ({ detail: 'Failed to send invitation' }));
        throw new Error(errorData.detail || errorData.message || 'Failed to send invitation');
      }
    } catch (error: any) {
      setInviteBanner({
        type: 'error',
        message: error?.message || 'We could not send the invitation. Please try again shortly.',
      });
    } finally {
      setIsSendingInvite(false);
    }
  };

  const handleDeleteClick = (partner: Partner, e: React.MouseEvent) => {
    e.stopPropagation();
    setDeleteDialog({ isOpen: true, partner });
  };

  const handleDeleteConfirm = async () => {
    if (!deleteDialog.partner) return;

    setIsDeleting(true);
    try {
      const response = await api.delete(`/api/v1/partners/${deleteDialog.partner.id}?confirm=DELETE`);
      
      if (response.ok) {
        setInviteBanner({
          type: 'success',
          message: `Partner ${deleteDialog.partner.first_name} ${deleteDialog.partner.last_name} has been deleted successfully.`,
        });
        setDeleteDialog({ isOpen: false, partner: null });
        await loadPartners();
      } else {
        const errorData = await response.json().catch(() => ({ detail: 'Failed to delete partner' }));
        throw new Error(errorData.detail || errorData.message || 'Failed to delete partner');
      }
    } catch (error: any) {
      setInviteBanner({
        type: 'error',
        message: error?.message || 'Failed to delete partner. Please try again.',
      });
      setDeleteDialog({ isOpen: false, partner: null });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialog({ isOpen: false, partner: null });
  };

  if (isLoadingPartners) {
    return (
      <AdminLayout pageTitle="Partner Management" pageDescription="Invite partners and manage account access">
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div
              className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-t-transparent mb-4"
              style={{ borderColor: COLORS.admin.primary, borderTopColor: 'transparent' }}
            />
            <p style={{ color: '#6B7280' }}>Loading partners...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout pageTitle="User Management" pageDescription="Invite partners and manage account access">
      <div style={{ backgroundColor: '#F5F5F5' }}>
        <div className="max-w-[1400px] mx-auto px-8 py-8">
          {inviteBanner && (
            <div
              className="mb-6 px-4 py-3 rounded-xl flex items-start gap-3"
              style={{
                backgroundColor: inviteBanner.type === 'success' ? SUCCESS_BANNER.background : ERROR_BANNER.background,
                border: `1px solid ${inviteBanner.type === 'success' ? SUCCESS_BANNER.border : ERROR_BANNER.border}`,
                color: inviteBanner.type === 'success' ? SUCCESS_BANNER.text : ERROR_BANNER.text,
              }}
            >
              <svg className="w-5 h-5 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {inviteBanner.type === 'success' ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                )}
              </svg>
              <div className="flex-1 text-sm leading-snug">{inviteBanner.message}</div>
              <button
                onClick={() => setInviteBanner(null)}
                className="flex-shrink-0 text-xs font-semibold uppercase tracking-wide"
                style={{ color: inviteBanner.type === 'success' ? SUCCESS_BANNER.text : ERROR_BANNER.text }}
              >
                Dismiss
              </button>
            </div>
          )}

          {/* Action Bar */}
          <div className="flex items-center justify-between mb-6">
            <div className="relative">
              <svg className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                type="text"
                placeholder="Search Partners"
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setCurrentPage(1);
                }}
                className="pl-9 pr-4 py-2 rounded-lg border focus:outline-none focus:ring-2 focus:ring-blue-500"
                style={{ borderColor: '#E5E7EB' }}
              />
            </div>
            <div className="flex items-center gap-3">
              <select
                value={filterStatus}
                onChange={(e) => {
                  setFilterStatus(e.target.value);
                  setCurrentPage(1);
                }}
                className="px-4 py-2 rounded-lg border focus:outline-none focus:ring-2 focus:ring-blue-500"
                style={{ borderColor: '#E5E7EB' }}
              >
                <option value="all">All Status</option>
                <option value="pending_approval">Pending Approval</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="suspended">Suspended</option>
              </select>
              <button
                onClick={() => setInviteModalOpen(true)}
                className="flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-white transition-all hover:shadow-md"
                style={{ backgroundColor: COLORS.admin.primary }}
              >
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
                </svg>
                Invite Partner
              </button>
            </div>
          </div>

          {/* Table View */}
          <div className="bg-white rounded-lg shadow-sm overflow-hidden">
            <table className="w-full">
              <thead style={{ backgroundColor: '#F9FAFB', borderBottom: '1px solid #E5E7EB' }}>
                <tr>
                  <th className="px-6 py-3 text-left text-sm font-semibold" style={{ color: '#6B7280' }}>
                    Partner
                  </th>
                  <th className="px-6 py-3 text-left text-sm font-semibold" style={{ color: '#6B7280' }}>
                    Email
                  </th>
                  <th className="px-6 py-3 text-left text-sm font-semibold" style={{ color: '#6B7280' }}>
                    Company
                  </th>
                  <th className="px-6 py-3 text-left text-sm font-semibold" style={{ color: '#6B7280' }}>
                    Invited
                  </th>
                  <th className="px-6 py-3 text-left text-sm font-semibold" style={{ color: '#6B7280' }}>
                    Status
                  </th>
                  <th className="px-6 py-3 text-right text-sm font-semibold" style={{ color: '#6B7280' }}>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {paginatedPartners.length > 0 ? (
                  paginatedPartners.map((partner) => {
                    const statusColors = getStatusColor(partner.status);
                    return (
                      <tr
                        key={partner.id}
                        className="border-b hover:bg-gray-50 transition-colors"
                        style={{ borderColor: '#E5E7EB' }}
                      >
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <div
                              className="w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-semibold"
                              style={{ backgroundColor: getInitialColor(partner.first_name + partner.last_name) }}
                            >
                              {getInitials(partner.first_name, partner.last_name, partner.email)}
                            </div>
                            <div>
                              <p className="font-medium text-sm" style={{ color: '#1F2937' }}>
                                {partner.first_name} {partner.last_name}
                              </p>
                              <p className="text-xs" style={{ color: '#9CA3AF' }}>
                                {partner.partner_id}
                              </p>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span className="text-sm" style={{ color: '#1F2937' }}>
                            {partner.email}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <span className="text-sm" style={{ color: '#1F2937' }}>
                            {partner.company_name || '—'}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <span className="text-sm" style={{ color: '#6B7280' }}>
                            {new Date(partner.invited_at).toLocaleDateString()}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <span
                            className="px-3 py-1 rounded-full text-xs font-medium"
                            style={{ backgroundColor: statusColors.bg, color: statusColors.text }}
                          >
                            {getStatusLabel(partner.status)}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={(e) => handleDeleteClick(partner, e)}
                              className="p-2 rounded-lg hover:bg-gray-100 transition-all"
                              title="Delete Partner"
                            >
                              <svg className="w-5 h-5" style={{ color: '#EF4444' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={6} className="px-6 py-12 text-center">
                      <p style={{ color: '#6B7280' }}>
                        {searchQuery || filterStatus !== 'all' 
                          ? 'No partners match your filters.' 
                          : 'No partners found. Invite a partner to get started.'}
                      </p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 mt-8">
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="px-4 py-2 rounded-lg border hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ borderColor: '#E5E7EB' }}
              >
                ← Previous
              </button>
              
              {[...Array(Math.min(10, totalPages))].map((_, i) => {
                const pageNum = i + 1;
                if (pageNum === 1 || pageNum === totalPages || (pageNum >= currentPage - 2 && pageNum <= currentPage + 2)) {
                  return (
                    <button
                      key={pageNum}
                      onClick={() => setCurrentPage(pageNum)}
                      className={`w-10 h-10 rounded-lg font-medium ${currentPage === pageNum ? 'text-white' : 'hover:bg-gray-50'}`}
                      style={{
                        backgroundColor: currentPage === pageNum ? COLORS.admin.primary : 'transparent',
                        color: currentPage === pageNum ? '#FFFFFF' : '#6B7280'
                      }}
                    >
                      {pageNum}
                    </button>
                  );
                } else if (pageNum === currentPage - 3 || pageNum === currentPage + 3) {
                  return <span key={pageNum} className="px-2">...</span>;
                }
                return null;
              })}

              <button
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                className="px-4 py-2 rounded-lg border hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ borderColor: '#E5E7EB' }}
              >
                Next →
              </button>
            </div>
          )}
        </div>
      </div>

      <InvitePartnerModal
        isOpen={isInviteModalOpen}
        onClose={() => {
          if (!isSendingInvite) setInviteModalOpen(false);
        }}
        onSend={handleInvitePartner}
        companies={companies}
        isLoadingCompanies={isLoadingCompanies}
        isSubmitting={isSendingInvite}
      />

      {/* Delete Confirmation Dialog */}
      {deleteDialog.isOpen && deleteDialog.partner && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#FEE2E2' }}>
                <svg className="w-6 h-6" style={{ color: '#DC2626' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-bold mb-2" style={{ color: '#1F2937' }}>
                  Delete Partner
                </h3>
                <p className="text-sm mb-4" style={{ color: '#6B7280' }}>
                  Are you sure you want to delete <strong>{deleteDialog.partner.first_name} {deleteDialog.partner.last_name}</strong>? 
                  This action cannot be undone and will permanently remove their account and all associated data.
                </p>
                <div className="flex items-center gap-3">
                  <button
                    onClick={handleDeleteCancel}
                    disabled={isDeleting}
                    className="flex-1 px-4 py-2 rounded-lg font-medium transition-all hover:bg-gray-100 disabled:opacity-50"
                    style={{ border: '1px solid #E5E7EB', color: '#374151' }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleDeleteConfirm}
                    disabled={isDeleting}
                    className="flex-1 px-4 py-2 rounded-lg font-medium text-white transition-all hover:opacity-90 disabled:opacity-50 flex items-center justify-center gap-2"
                    style={{ backgroundColor: '#DC2626' }}
                  >
                    {isDeleting ? (
                      <>
                        <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                        Deleting...
                      </>
                    ) : (
                      'Delete Partner'
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

